
import * as GeoJSON from 'geojson';
import { Theme } from '@routific/hive';
import { IColor } from '../../../types/IColor';
import {
  MapMarkerModelType, VisitLocationDtoModelType,
} from '../../../store/mst/models';
import MarkerIconLayer from '../../map/layers/MarkerIconLayer';
import OrderAttemptMarkerLayer from '../../map/layers/OrderAttemptMarkerLayer';
import { GREY_RGB, LayerIds } from '../../map/constants';
import getScaledColor from '../../../helpers/getScaledColor';
import { isValidCoordinate } from '../../../helpers/validateCoordinates';
import { VisitStatus } from '../../../types/TVisit';
import { MAP_DEFAULT_ZOOM, PinDropMode } from '.';
import { CursorMode } from '../../shared/MapBase/utils/getMapCursor';

export interface OrderAttemptLocation {
  id: string;
  latitude: number;
  longitude: number;
  isMissed?: boolean;
}

export const getData = (currentLocations?: VisitLocationDtoModelType[]) => {
  const markers: MapMarkerModelType[] = [];

  currentLocations?.forEach((location, index) => {
    markers.push({
      id: location.id,
      sequence: currentLocations?.length > 1 ? index + 1 : undefined,
      type: 'delivering_stop',
      longitude: location.longitude,
      latitude: location.latitude,
      color: {
        hex: '#999999',
        rgb: GREY_RGB,
      } as IColor,
    } as MapMarkerModelType);
  });

  // @ts-ignore
  return GeoJSON.parse(
    markers, { Point: ['latitude', 'longitude'] },
  ).features;
};

export const getLocationIconLayer = (
  locations: VisitLocationDtoModelType[] | undefined,
  handleLocationHover: any,
  handleLocationClick: any,
  mapViewState: any,
  hoveredLocation: VisitLocationDtoModelType | undefined,
  selectedLocation: VisitLocationDtoModelType | undefined,
) => new MarkerIconLayer({
  id: LayerIds.STOP_SCATTERPLOT_SUGGESTIONS,
  data: getData(locations),
  onHover: handleLocationHover,
  onClick: handleLocationClick,
  visible: true,
  hoveredMarker: hoveredLocation,
  selectedMarker: selectedLocation,
  zoom: mapViewState?.zoom ?? MAP_DEFAULT_ZOOM,
  enableClustersAtSameLocation: false,
});

export const getOrderAttemptData = (orderAttemptLocations?: OrderAttemptLocation[]) => {
  if (!orderAttemptLocations?.length) {
    return [];
  }

  const markers = orderAttemptLocations
    .filter((location) => isValidCoordinate(location.latitude) && isValidCoordinate(location.longitude))
    .map((orderAttemptLocation) => ({
      id: orderAttemptLocation.id,
      type: orderAttemptLocation.isMissed ? 'skipped' : 'done',
      status: VisitStatus.DEPARTED,
      longitude: orderAttemptLocation.longitude,
      latitude: orderAttemptLocation.latitude,
      color: getScaledColor(
        orderAttemptLocation.isMissed ? Theme.Colors.warning6 : Theme.Colors.success6,
      ),
    } as MapMarkerModelType));

  // @ts-ignore
  return GeoJSON.parse(
    markers, { Point: ['latitude', 'longitude'] },
  ).features;
};

export const getOrderAttemptIconLayer = (
  orderAttemptLocations: OrderAttemptLocation[] | undefined,
) => {
  const data = getOrderAttemptData(orderAttemptLocations);
  if (!data.length) {
    return null;
  }

  return new OrderAttemptMarkerLayer({
    id: LayerIds.ORDER_ATTEMPT_LOCATION,
    data,
    visible: true,
  });
};

export const getCursor = (localPinMode?: PinDropMode, hoveredLocation?: VisitLocationDtoModelType): CursorMode => {
  switch (localPinMode) {
    case PinDropMode.EDITING:
      return CursorMode.LASSO;
    case PinDropMode.VIEWING:
    default:
      if (!!hoveredLocation) {
        return CursorMode.POINTER;
      }
      return CursorMode.DEFAULT;
  }
};

export const handleOnClear = (allowPinDrop: boolean, updatePinDropMode: (mode?: any) => void, localPinMode?: PinDropMode) => {
  if (allowPinDrop && localPinMode === PinDropMode.EDITING) {
    updatePinDropMode(PinDropMode.VIEWING);
  }
};
